import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check, AlertTriangle, Lock, RotateCcw,
  Cpu, Cog, CircuitBoard, MemoryStick, HardDrive, Zap, Package, Fan, Plus,
} from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/api";
import { ReportIssueModal } from "@/components/ReportIssueModal";

// Cyan component icon lookup — no new dependency, uses existing lucide-react.
const ICON_FOR = {
  cpu: Cpu, gpu: Cog, motherboard: CircuitBoard,
  ram: MemoryStick, storage: HardDrive, storage2: HardDrive,
  power_supply: Zap, case: Package, cpu_cooler: Fan, fans: Fan, os: Package,
};
function IconFor({ componentKey, size = 15 }) {
  // OTHERS entries use synthetic keys like `others:0`, `others:1` → Package.
  const isOthers = typeof componentKey === "string" && componentKey.startsWith("others");
  const Ico = isOthers ? Package : (ICON_FOR[componentKey] || Package);
  const isStorage2 = componentKey === "storage2";
  return (
    <div
      className="relative flex items-center justify-center shrink-0 rounded-md"
      style={{
        width: 34, height: 34,
        background: "rgba(0,229,255,0.08)",
        border: "1px solid rgba(0,229,255,0.22)",
        color: "var(--accent)",
      }}
    >
      <Ico size={size} />
      {isStorage2 && (
        <span
          className="absolute -top-1 -right-1 rounded-full flex items-center justify-center"
          style={{ width: 12, height: 12, background: "var(--accent)", color: "#000" }}
        >
          <Plus size={9} strokeWidth={3} />
        </span>
      )}
    </div>
  );
}

/**
 * Pre-build component verification card. Rendered on the technician station
 * ONLY while the order is in WAITING. It is strictly additive:
 *   - Fetches `/api/orders/{id}/component-verifications` (list per component)
 *   - Provides per-row [✓ VERIFY] and [⚠ REPORT ISSUE] actions
 *   - Reports back to the parent (via onReady) whether START BUILD is allowed
 *
 * Does NOT touch: streaming, chat, WhatsApp, testing phase, lifecycle mutations.
 */
export function ComponentVerificationCard({ orderId, onReady, readOnly = false }) {
  const [state, setState] = useState({ loading: true, items: [], open_issues: [] });
  const [busyKey, setBusyKey] = useState(null);
  const [modal, setModal] = useState(null); // { key, label, value }

  const load = useCallback(async () => {
    try {
      const { data } = await client.get(`/orders/${orderId}/component-verifications`);
      setState({ loading: false, items: data.items || [], open_issues: data.open_issues || [] });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [orderId, load]);

  const verified = state.items.filter((i) => i.status === "VERIFIED").length;
  const total = state.items.length;
  const openIssueCount = state.open_issues.length;
  // Orders with no components have nothing to verify (backend gate is a no-op too).
  const allVerified = !state.loading && verified === total && openIssueCount === 0;

  useEffect(() => {
    onReady && onReady({ allVerified, verified, total, openIssueCount });
  }, [allVerified, verified, total, openIssueCount, onReady]);

  const verify = async (key) => {
    setBusyKey(key);
    try {
      await client.post(`/orders/${orderId}/component-verifications/${key}/verify`);
      toast.success("Component verified");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cannot verify");
    } finally { setBusyKey(null); }
  };

  return (
    <div
      className="rounded-xl border p-5"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }}
      data-testid="component-verification-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-semibold">Component Verification</div>
        {total > 0 && (
          <div
            className="text-[10px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-md"
            style={{
              color: allVerified ? "#00ff88" : (openIssueCount > 0 ? "#ff9800" : "#a1a1aa"),
              background: allVerified ? "rgba(0,255,136,0.08)" : (openIssueCount > 0 ? "rgba(255,152,0,0.08)" : "rgba(255,255,255,0.04)"),
            }}
            data-testid="verify-progress"
          >
            {verified} / {total} verified
          </div>
        )}
      </div>

      {state.loading && (
        <div className="text-white/40 text-sm py-2">Loading components…</div>
      )}

      {!state.loading && total === 0 && (
        <div className="text-white/40 text-sm py-2">No components on this order.</div>
      )}

      {openIssueCount > 0 && (
        <div
          className="mb-3 px-3 py-2 rounded-md text-[11px] font-semibold flex items-center gap-2"
          style={{ background: "rgba(255,152,0,0.08)", color: "#ffb857", border: "1px solid rgba(255,152,0,0.25)" }}
          data-testid="verify-issue-banner"
        >
          <AlertTriangle size={13} />
          {openIssueCount} component issue{openIssueCount > 1 ? "s" : ""} require{openIssueCount === 1 ? "s" : ""} attention
        </div>
      )}

      <div className="space-y-1.5">
        {state.items.map((it) => {
          const status = it.status || "PENDING";
          const isVerified = status === "VERIFIED";
          const isIssue    = status === "ISSUE_REPORTED";
          return (
            <div
              key={it.component_key}
              className="rounded-md border px-3 py-2"
              style={{
                borderColor: isVerified ? "rgba(0,255,136,0.22)" : isIssue ? "rgba(255,152,0,0.28)" : "rgba(255,255,255,0.06)",
                background:  isVerified ? "rgba(0,255,136,0.04)" : isIssue ? "rgba(255,152,0,0.05)" : "transparent",
              }}
              data-testid={`verify-row-${it.component_key}`}
            >
              <div className="flex items-center gap-3">
                <IconFor componentKey={it.component_key} />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 font-semibold flex items-center gap-1.5">
                    <span>{it.label}</span>
                    {it.qty && Number(it.qty) > 0 && (
                      <span className="text-white/70 font-bold" data-testid={`verify-qty-${it.component_key}`}>×{Number(it.qty)}</span>
                    )}
                  </div>
                  <div className="text-white text-sm font-medium truncate mt-0.5" title={it.value}>{it.value || "—"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isVerified && (
                    <div className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.18em] uppercase px-2 py-1 rounded-md" style={{ background: "rgba(0,255,136,0.12)", color: "#00ff88" }}>
                      <Check size={11} /> Verified
                    </div>
                  )}
                  {isIssue && (
                    <div className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.18em] uppercase px-2 py-1 rounded-md" style={{ background: "rgba(255,152,0,0.15)", color: "#ffb857" }}>
                      <AlertTriangle size={11} /> Issue Reported
                    </div>
                  )}
                  {!readOnly && !isVerified && !isIssue && (
                    <>
                      <button
                        type="button"
                        disabled={busyKey === it.component_key}
                        onClick={() => verify(it.component_key)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.18em] uppercase px-2.5 py-1.5 rounded-md disabled:opacity-40"
                        style={{ background: "rgba(0,255,136,0.10)", color: "#00ff88", border: "1px solid rgba(0,255,136,0.28)" }}
                        data-testid={`verify-btn-${it.component_key}`}
                      >
                        <Check size={12} /> Verify
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal({ key: it.component_key, label: it.label, value: it.value })}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.18em] uppercase px-2.5 py-1.5 rounded-md"
                        style={{ background: "rgba(255,152,0,0.10)", color: "#ffb857", border: "1px solid rgba(255,152,0,0.28)" }}
                        data-testid={`report-issue-btn-${it.component_key}`}
                      >
                        <AlertTriangle size={12} /> Report Issue
                      </button>
                    </>
                  )}
                  {!readOnly && isIssue && (
                    <div className="text-[10px] text-white/40 italic">Awaiting supervisor…</div>
                  )}
                  {!readOnly && isVerified && (
                    <button
                      type="button"
                      title="Undo verification"
                      onClick={() => setModal({ key: it.component_key, label: it.label, value: it.value })}
                      className="text-white/30 hover:text-white/70"
                      data-testid={`report-issue-btn-${it.component_key}`}
                    >
                      <RotateCcw size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly && total > 0 && !allVerified && (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-white/45">
          <Lock size={11} />
          Resolve all component issues before starting the build.
        </div>
      )}

      <ReportIssueModal
        open={!!modal}
        onClose={() => setModal(null)}
        orderId={orderId}
        componentKey={modal?.key}
        componentLabel={modal?.label}
        componentValue={modal?.value}
        onSubmitted={load}
      />
    </div>
  );
}

export default ComponentVerificationCard;
